import React, { useState, useEffect, useMemo } from "react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { getStockByBranch, deleteStock } from "../../services/StockApiServices";
import { useSelector } from "react-redux";
import { InvStockAdjustment } from "../../core/json/invStockAdjust";
import StockAdjustmentModal from "../../components/transactions/modal/StockAdjustmentModal";
import { TransactionScreen } from "../../components/transactions/list";
import { dateOf, dmy, intg, num } from "../../components/report/reportFormat";

// adjustment `type`, labelled the way the old list badge did
const ADJ_TYPES = [
  { v: 0, t: "Stock Adjustment", tone: "credit" },
  { v: 1, t: "Damage", tone: "danger" },
  { v: 2, t: "Wastage", tone: "card" },
  { v: 3, t: "Credit", tone: "danger" },
  { v: 4, t: "UPI", tone: "upi" },
];

const StockAdjustment = () => {
  const [updateData, setUpdateData] = useState([]);
  const [modalMode, setModalMode] = useState("add");
  const [modalOpen, setModalOpen] = useState(false);
  const [stockList, setStockList] = useState([]);

  // Get branch from redux
  const { branch } = useSelector((state) => state.branchlist);
  // Get user from redux
  const { userId } = useSelector((state) => state.userauth);
  const { menuItems } = useSelector((state) => state.amc);
  const [permit, setpermit] = useState();
  useEffect(() => {
    setpermit(
      menuItems?.menuPermissions?.find((e) => e.menu.name == "Stock Adjustment")
    );
  }, []);

  useEffect(() => {
    handlestockData();
  }, [branch]);

  const handlestockData = async () => {
    try {
      const response = await getStockByBranch(branch);
      const stockData = Array.isArray(response.data) ? response.data : [];
      setStockList(stockData);
    } catch (error) {
      console.error(
        "Failed to fetch Stock adjustments",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  const MySwal = withReactContent(Swal);

  // Handle the click event to delete a Stock adjustments
  const handleDeleteClick = (obj) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const objStockAdj = new InvStockAdjustment();
          objStockAdj.id = obj.id;
          objStockAdj.guid = obj.guid;
          objStockAdj.branch = branch;
          objStockAdj.type = 0;
          // objStockAdj.isDeleted = true;
          objStockAdj.updatedUser = `${userId?.id}`;
          const response = await deleteStock(objStockAdj);
          if (response?.data === true) {
            MySwal.fire({
              title: "Deleted!",
              text: "The Stock Adjustment has been deleted.",
              icon: "success",
              confirmButtonColor: "#00ff00",
              confirmButtonText: "OK",
            }).then(() => {
              handlestockData();
            });
          } else {
            MySwal.fire({
              title: "Error!",
              text: "Something went wrong.",
              icon: "error",
              confirmButtonColor: "#ff0000",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          console.error(
            "Error deleting reason:",
            error?.response?.data?.Message || "something went wrong"
          );
          MySwal.fire({
            title: "Error!",
            text: error?.response?.data?.Message || "something went wrong",
            icon: "error",
            confirmButtonColor: "#ff0000",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const handleEditClick = async (guid) => {
    try {
      const response = getStockAdjByGuid(guid);

      setUpdateData(response);
      setModalMode("edit");
      setModalOpen(true);
    } catch (error) {
      console.error(
        "Error fetching Stock adjustment by GUID",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  function getStockAdjByGuid(guid) {
    const filteredStock = stockList?.filter(
      (stockAdj) => stockAdj.guid === guid
    );

    return filteredStock[0];
  }

  /* ---------------- list ---------------- */
  // one flat record per adjustment; the keys match the columns
  const rows = useMemo(
    () =>
      stockList.map((entry) => {
        const lines = entry?.stockAdjustmentDetails || [];
        const type = ADJ_TYPES.find((t) => t.v === entry?.type);
        return {
          guid: entry.guid,
          raw: entry,
          date: dateOf(entry?.date),
          billNo: entry?.no || "",
          typeValue: entry?.type,
          type: type?.t || "Unknown",
          remarks: entry?.remarks || "",
          items: lines.length,
          adjQty: lines.reduce((a, l) => a + num(l?.adjQuantity), 0),
          lines,
        };
      }),
    [stockList]
  );

  const columns = useMemo(
    () => [
      { k: "date", t: "Date", type: "date", ago: true },
      { k: "billNo", t: "Entry No", type: "doc" },
      {
        k: "type",
        t: "Type",
        type: "chip",
        chip: (r) => ({ text: r.type, tone: ADJ_TYPES.find((t) => t.v === r.typeValue)?.tone }),
      },
      { k: "remarks", t: "Remarks" },
      { k: "items", t: "Items", type: "int", off: true },
      { k: "adjQty", t: "Adj Quantity", type: "int", off: true },
    ],
    []
  );

  const tabs = useMemo(() => [{ id: "all", label: "Stock Adjustment" }], []);

  const filters = useMemo(
    () => [
      {
        key: "types",
        label: "Type",
        items: ADJ_TYPES.slice(0, 3).map((t) => ({ v: t.v, t: t.t })),
        match: (r, v) => v.includes(r.typeValue),
      },
    ],
    []
  );

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.remarks) ||
      hit(r.type) ||
      hit(r.date) ||
      r.lines.some((l) => hit(l?.product?.name) || hit(l?.product?.code))
    );
  };

  const drawerContent = (r) => ({
    title: r.type,
    subtitle: [r.billNo, dmy(r.date)].filter(Boolean).join("  ·  "),
    avatarKey: r.type,
    meta: [
      { k: "Type", v: r.type },
      { k: "Date", v: dmy(r.date) },
      { k: "Remarks", v: r.remarks },
      { k: "Items", v: intg(r.items) },
    ],
    lines: {
      title: "Adjustment lines",
      columns: [
        { t: "Item" },
        { t: "Batch" },
        { t: "Qty", align: "right" },
        { t: "Adj Qty", align: "right" },
      ],
      rows: r.lines.map((l) => [
        <>
          <div className="axr-nm">{l?.product?.name || "Unknown Item"}</div>
          <div className="axr-cd">{[l?.product?.code, l?.unit?.name].filter(Boolean).join(" · ")}</div>
        </>,
        l?.batch || "—",
        intg(l?.baseQuantity),
        intg(l?.adjQuantity),
      ]),
    },
    totals: [{ l: "Total adj quantity", v: intg(r.adjQty) }],
  });

  return (
    <TransactionScreen
      title="Stock Adjustment"
      subtitle="Manage Stock Adjustments here"
      unit="adjustments"
      rows={rows}
      columns={columns}
      tabs={tabs}
      filters={filters}
      searchMatch={searchMatch}
      searchPlaceholder="Entry no, type, remarks, item…"
      sortOptions={[
        { v: "date:-1", t: "Newest first" },
        { v: "date:1", t: "Oldest first" },
        { v: "billNo:-1", t: "Entry no (high–low)" },
        { v: "billNo:1", t: "Entry no (low–high)" },
      ]}
      exportName="Stock adjustment"
      onRefresh={handlestockData}
      canAdd={permit?.add}
      newLabel="New adjustment"
      onNew={() => {
        setModalMode("add");
        setModalOpen(true);
      }}
      canEdit={permit?.edit}
      onEdit={(r) => handleEditClick(r.guid)}
      editLabel="Edit adjustment"
      canDelete={permit?.deletion}
      onDelete={(r) => handleDeleteClick(r.raw)}
      drawerContent={drawerContent}
      keysDisabled={modalOpen}
    >
      {modalOpen && (
        <StockAdjustmentModal
          mode={modalMode}
          data={modalMode === "edit" ? updateData : {}}
          handleClose={() => setModalOpen(false)}
          handleRefresh={() => {
            handlestockData();
          }}
        />
      )}
    </TransactionScreen>
  );
};

export default StockAdjustment;
